import type { ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { ApiError, NotYetAvailableError } from "../../api/errors";
import { Icon } from "../Icon";
import { Banner } from "./Banner";
import { Button } from "./Button";
import { RefId } from "./CopyField";
import { SkeletonBlock } from "./Skeleton";

// UX-DESIGN.md §6 "Shared system states, not ad hoc per screen": loading,
// empty, retryable error, permission-denied, not-yet-available. Every
// screen uses these, so they look and read the same everywhere.

/** Placeholder for a screen/section whose endpoint does not exist yet. */
export function NotAvailable({
  what,
  endpoint,
  gapId,
  children,
}: {
  what: ReactNode;
  endpoint: string;
  gapId: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty" role="status">
      <Icon name="construction" className="empty-icon" />
      <h3>Not yet available: {what}</h3>
      <p>
        {children ?? "This part of the portal is designed but the service it needs hasn't been built yet. No data is shown rather than a guess."}
      </p>
      <p className="xs">
        Needs <code>{endpoint}</code> · tracked as <strong>{gapId}</strong> in web/API-GAPS.md
      </p>
    </div>
  );
}

export function ErrorState({ error, onRetry, financial }: { error: unknown; onRetry?: () => void; financial?: boolean }) {
  if (error instanceof NotYetAvailableError) {
    return <NotAvailable what="This information" endpoint={error.endpoint} gapId={error.gapId} />;
  }
  const apiErr = error instanceof ApiError ? error : null;
  const message = apiErr?.message ?? (error instanceof Error ? error.message : "Something went wrong.");
  const tone = apiErr?.kind === "forbidden" || apiErr?.kind === "not_found" ? "warning" : "error";
  return (
    <Banner
      tone={tone}
      title={apiErr?.kind === "forbidden" ? "You don't have access" : apiErr?.kind === "not_found" ? "Not found" : "Couldn't load this"}
    >
      <div className="stack-sm">
        <span>{message}</span>
        {financial && apiErr?.idempotencyKey ? <RefId value={apiErr.idempotencyKey} /> : null}
        {onRetry && (apiErr?.retryable ?? true) ? (
          <div>
            <Button variant="secondary" size="sm" onClick={onRetry}>
              Try again
            </Button>
          </div>
        ) : null}
        {apiErr?.retryable ? (
          <span className="xs muted">If this keeps happening, contact support and mention the time it happened.</span>
        ) : null}
      </div>
    </Banner>
  );
}

/** Renders loading/error for a query, then children with the data. */
export function QueryView<T>({
  query,
  children,
  loadingLabel,
  lines,
}: {
  query: UseQueryResult<T>;
  children: (data: T) => ReactNode;
  loadingLabel?: string;
  lines?: number;
}) {
  if (query.isPending) return <SkeletonBlock label={loadingLabel ?? "Loading"} lines={lines ?? 3} />;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  return <>{children(query.data)}</>;
}
